import React, { useState, useEffect } from 'react';
import { X, Download, AlertCircle, CheckCircle, Clock } from 'lucide-react';

const ACTIVE_JOB_STATUSES = [
  'processing',
  'queued',
  'picked',
  'preparing',
  'generating',
  'encoding',
  'finalizing',
  'pending'
];

const VideoExportProgress = ({ jobs, onClose, onDownload, onCancel, onRemove }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Show notification when there are active jobs
    setIsVisible(jobs.length > 0);
  }, [jobs]);

  if (!isVisible || jobs.length === 0) {
    return null;
  }

  const sortedJobs = [...jobs].sort((a, b) => {
    const timeA = a?.created_at ? new Date(a.created_at).getTime() : 0;
    const timeB = b?.created_at ? new Date(b.created_at).getTime() : 0;
    if (timeA && timeB && timeA !== timeB) {
      return timeB - timeA;
    }
    return (b.id || 0) - (a.id || 0);
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'queued':
        return <Clock className="w-5 h-5 text-gray-500" />;
      case 'picked':
        return <Clock className="w-5 h-5 text-blue-500 animate-pulse" />;
      case 'preparing':
        return <Clock className="w-5 h-5 text-blue-500 animate-spin" />;
      case 'generating':
        return <Clock className="w-5 h-5 text-purple-500 animate-spin" />;
      case 'encoding':
        return <Clock className="w-5 h-5 text-orange-500 animate-spin" />;
      case 'finalizing':
        return <Clock className="w-5 h-5 text-green-400 animate-spin" />;
      case 'processing': // Legacy status support
        return <Clock className="w-5 h-5 text-blue-500 animate-spin" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-50 border-green-200';
      case 'failed':
        return 'bg-red-50 border-red-200';
      case 'queued':
        return 'bg-gray-50 border-gray-200';
      case 'picked':
        return 'bg-blue-50 border-blue-200';
      case 'preparing':
        return 'bg-blue-50 border-blue-200';
      case 'generating':
        return 'bg-purple-50 border-purple-200';
      case 'encoding':
        return 'bg-orange-50 border-orange-200';
      case 'finalizing':
        return 'bg-green-50 border-green-200';
      case 'processing': // Legacy status support
        return 'bg-blue-50 border-blue-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const getStatusMessage = (status, progress = 0) => {
    switch (status) {
      case 'queued':
        return '📋 Your video is waiting in the queue';
      case 'picked':
        return '🎯 Worker has picked up your video job';
      case 'preparing':
        return '🔧 Analyzing your template and preparing';
      case 'generating':
        return '🎬 Creating video frames from your design';
      case 'encoding':
        return '🎥 Converting frames into video format';
      case 'finalizing':
        return '✨ Final processing and preparing download';
      case 'completed':
        return '🎉 Your video is ready for download!';
      case 'failed':
        return '❌ Video processing failed';
      case 'processing': // Legacy status support
        return 'Processing your video...';
      case 'pending': // Legacy status support
        return 'Waiting in queue...';
      default:
        return 'Processing...';
    }
  };

  const getProgressBarColor = (status) => {
    switch (status) {
      case 'queued':
        return 'bg-gray-400';
      case 'picked':
        return 'bg-blue-400';
      case 'preparing':
        return 'bg-blue-500';
      case 'generating':
        return 'bg-purple-500';
      case 'encoding':
        return 'bg-orange-500';
      case 'finalizing':
        return 'bg-green-400';
      case 'completed':
        return 'bg-green-500';
      default:
        return 'bg-blue-500';
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true });
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm">
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-sm font-medium text-gray-900">Video Export Progress</h3>
          <button
            onClick={() => setIsVisible(false)}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Jobs List */}
        <div className="max-h-80 overflow-y-auto">
          {sortedJobs.map((job) => (
            <div
              key={job.id}
              className={`p-4 border-b border-gray-100 last:border-b-0 ${getStatusColor(job.status)}`}
            >
              {/* Job Header */}
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  {getStatusIcon(job.status)}
                  <div className="flex items-baseline gap-1 text-gray-900">
                    <span className="text-sm font-semibold leading-none">Video Export</span>
                    <span className="text-xs font-semibold text-gray-600 leading-none">#{job.id}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {ACTIVE_JOB_STATUSES.includes(job.status) && onCancel && (
                    <button
                      onClick={() => onCancel(job.id)}
                      className="text-xs text-red-600 hover:text-red-800 transition-colors px-2 py-1 rounded hover:bg-red-50"
                    >
                      Cancel
                    </button>
                  )}
                  {!ACTIVE_JOB_STATUSES.includes(job.status) && onRemove && (
                    <button
                      onClick={() => onRemove(job.id)}
                      className="text-xs text-gray-500 hover:text-gray-700 transition-colors px-2 py-1 rounded hover:bg-gray-100"
                    >
                      Dismiss
                    </button>
                  )}
                </div>
              </div>

              {/* Progress Bar - Show for all active statuses */}
              {job.status !== 'completed' && job.status !== 'failed' && (
                <div className="mb-3">
                  <div className="flex items-center justify-between text-xs text-gray-600 mb-2">
                    <span className="font-medium">Progress</span>
                    <span className="font-mono bg-gray-100 px-2 py-1 rounded">
                      {Math.round(job.progress || 0)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3 shadow-inner">
                    <div
                      className={`h-3 rounded-full transition-all duration-500 ease-out ${getProgressBarColor(job.status)}`}
                      style={{
                        width: `${Math.max(job.progress || 0, job.status === 'queued' ? 5 : 10)}%`,
                        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1)'
                      }}
                    >
                      {/* Progress bar shine effect */}
                      <div className="h-full w-full rounded-full bg-gradient-to-r from-transparent via-white to-transparent opacity-30"></div>
                    </div>
                  </div>
                </div>
              )}

              {/* Status Message */}
              <div className="mb-3">
                <div className="text-sm text-gray-700 font-medium mb-1">
                  {job.status === 'failed' ? (job.error_message || 'Export failed') : getStatusMessage(job.status, job.progress)}
                </div>
                {/* Additional status details from database */}
                {job.progress_text && (
                  <div className="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded">
                    {job.progress_text}
                  </div>
                )}
              </div>

              {/* Timestamps */}
              {(job.created_at || job.completed_at) && (
                <div className="text-xs text-gray-500 mb-2 flex flex-wrap gap-x-4 gap-y-1">
                  {job.created_at && (
                    <span>Started: {formatTime(job.created_at)}</span>
                  )}
                  {job.completed_at && (
                    <span>Completed: {formatTime(job.completed_at)}</span>
                  )}
                </div>
              )}

              {/* Actions */}
              {job.status === 'completed' && job.video_url && (
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => onDownload(job.video_url, job.id)}
                    className="flex items-center gap-1 px-3 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 transition-colors"
                  >
                    <Download className="w-3 h-3" />
                    Download
                  </button>
                  <a
                    href={job.video_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 transition-colors"
                  >
                    Open
                  </a>
                  {onRemove && (
                    <button
                      onClick={() => onRemove(job.id)}
                      className="px-3 py-1 bg-gray-200 text-gray-700 text-xs rounded hover:bg-gray-300 transition-colors"
                    >
                      Remove
                    </button>
                  )}
                </div>
              )}

              {job.status === 'failed' && onRemove && (
                <button
                  onClick={() => onRemove(job.id)}
                  className="px-3 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 transition-colors"
                >
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-4 py-2 border-t border-gray-200">
          <div className="text-xs text-gray-500 text-center">
            {(() => {
              const activeJobs = jobs.filter(j =>
                ['queued', 'picked', 'preparing', 'generating', 'encoding', 'finalizing', 'processing', 'pending'].includes(j.status)
              ).length;
              const completedJobs = jobs.filter(j => j.status === 'completed').length;
              const failedJobs = jobs.filter(j => j.status === 'failed').length;

              return `${activeJobs} active • ${completedJobs} completed • ${failedJobs} failed`;
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoExportProgress;
